import React, { FormEvent } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import theme from './theme';
import Tooltip from '@vromo/ui/components/Tooltip';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { qaLabelDispatch } from '../../../common-qa-labels/dispatch';
import { buildDynamicLabel } from '../../../common-qa-labels/utils';

type WrapperProps = {
    direction: 'row' | 'column';
};

const Wrapper = styled.div<WrapperProps>`
    display: flex;
    flex-direction: ${(props) => (props.direction ? props.direction : 'column')};
    height: auto;
    width: 100%;
    box-sizing: border-box;

    label {
        padding: ${({ direction }) => (direction === 'column' ? '8px 0px' : '0px 10px 0px 0px')};
    }

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const RadioButtonLabel = styled.label`
    display: block;
    cursor: pointer;
    input[type='radio'] {
        display: none;
        & + span {
            line-height: 18px;
            padding-left: 18px;
            position: relative;
            font-weight: 400;

            display: flex;
            align-items: center;

            &:not(:empty) {
                padding-left: 25px;
            }
            &:before,
            &:after {
                content: '';
                width: 18px;
                height: 18px;
                display: block;
                border-radius: 50%;
                left: 0;
                position: absolute;
            }
            &:before {
                background: ${theme.grey};
                transition: background 0.2s ease, transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 2);
            }
            &:after {
                background: #fff;
                transform: scale(0.78);
                transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.4);
            }
        }
        &:checked + span {
            &:before {
                transform: scale(1.04);
                background: ${theme.primary};
            }
            &:after {
                transform: scale(0.4);
                transition: transform 0.2s ease;
            }
        }
    }
    &:hover {
        input {
            & + span {
                &:before {
                    transform: scale(0.92);
                }
                &:after {
                    transform: scale(0.74);
                }
            }
            &:checked + span {
                &:after {
                    transform: scale(0.4);
                }
            }
        }
    }
`;

type Props = {
    options: Array<{
        label: string;
        value: any;
        tooltip?: string;
        /** optionally render a component rather than the given label, as part of the radio option */
        component?: JSX.Element;
    }>;
    direction?: 'row' | 'column';
    onChange: JSX.IntrinsicElements['input']['onChange'];
    checked: any;
    disabled?: boolean;
    name?: string;
    labelStyle?: React.CSSProperties;
};

export default observer<Props>(function RadioGroup({
    options,
    direction = 'column',
    onChange,
    checked,
    disabled = false,
    name = 'radio',
    labelStyle
}) {
    return (
        <Wrapper direction={direction}>
            {options.map((opt) => (
                <RadioButtonLabel
                    style={labelStyle}
                    key={opt.value}
                    data-testid={buildDynamicLabel(qaLabelDispatch.newJobModal.taskModal.taskTypeRadioBtn, opt.value)}>
                    <input
                        type="radio"
                        name={name}
                        value={opt.value}
                        checked={checked === opt.value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                    {opt.tooltip?.length ? (
                        <Tooltip ariaLabel={opt.label} position="bottom" label={opt.tooltip}>
                            <span>{opt.label}</span>
                        </Tooltip>
                    ) : (
                        <span>{opt.component || opt.label}</span>
                    )}
                </RadioButtonLabel>
            ))}
        </Wrapper>
    );
});
